import React, { useEffect, useState, ChangeEvent } from 'react'

import { Modal } from '@components/common/modal'
import { FlexContainer } from '@components/common'
import {
  Button,
  Checkbox,
  CurrencyInput,
  LinkText,
} from '@components/common/form'

import {
  AccountCustomServiceFee,
  GroupOrderCustomServiceFee,
  GroupOrderCustomServiceFeeSettings,
} from '@types'

const getDefaultConfig = (account: {
  accountCustomServiceFees: AccountCustomServiceFee[]
}): GroupOrderCustomServiceFeeSettings => {
  return {
    serviceFees: account.accountCustomServiceFees.map((cs) => ({
      serviceFeeType: cs.serviceFeeType,
      feeDescription: cs.feeDescription,
      valueCalculationType: cs.valueCalculationType,
      value: cs.value,
      percentValue: cs.percentValue,
      isTaxExempt: cs.isTaxExempt,
      itemTaxCode: cs.itemTaxCode,
    })),
  }
}

const parseRawConfig = (config: any) => {
  return {
    serviceFees: config.serviceFees.map((fee: any) => {
      if (fee.valueCalculationType === 'Flat Price') {
        return {
          ...fee,
          value: parseFloat(fee.value),
        }
      }

      return {
        ...fee,
        percentValue: parseFloat(fee.percentValue),
      }
    }),
  }
}

const getLocationConfigs = (
  account: {
    accountCustomServiceFees: AccountCustomServiceFee[]
  },
  clientSettingsCollection: {
    id: string
    name: string
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings | null
  }[],
  initializeEmpty: boolean,
) => {
  const locationConfigs: Record<
    string,
    {
      id: string
      name: string
      config: GroupOrderCustomServiceFeeSettings
    }
  > = {}
  clientSettingsCollection.forEach((cs) => {
    const locationConfig = cs.customServiceFeeSettings
    const config =
      locationConfig && !initializeEmpty
        ? parseRawConfig(locationConfig)
        : getDefaultConfig(account)
    locationConfigs[cs.id] = {
      id: cs.id,
      name: cs.name,
      config,
    }
  })

  return locationConfigs
}

const initForm = (
  account: {
    accountCustomServiceFees: AccountCustomServiceFee[]
  },
  clientGlobalSettings: {
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings | null
  },
  clientSettingsCollection: {
    id: string
    name: string
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings | null
  }[],
) => {
  const globalConfig = clientGlobalSettings.customServiceFeeSettings
  const hasGlobalCustomServiceFees = Boolean(globalConfig)
  const locationHasCustomServiceFees = clientSettingsCollection.some((cs) =>
    Boolean(cs.customServiceFeeSettings),
  )

  return {
    globalCustomServiceFeesEnabled: hasGlobalCustomServiceFees,
    globalConfig: globalConfig
      ? parseRawConfig(globalConfig)
      : getDefaultConfig(account),
    locationCustomServiceFeesEnabled:
      !hasGlobalCustomServiceFees && locationHasCustomServiceFees,
    locationConfigs: getLocationConfigs(
      account,
      clientSettingsCollection,
      false,
    ),
  }
}

const validateConfig = (config: GroupOrderCustomServiceFeeSettings) => {
  const errors: string[] = []
  config.serviceFees.forEach((serviceFee) => {
    if (serviceFee.valueCalculationType === 'Flat Price') {
      if (
        !serviceFee.value ||
        isNaN(serviceFee.value) ||
        serviceFee.value <= 0
      ) {
        errors.push('Value must be greater than 0')
      }
    } else if (serviceFee.valueCalculationType === 'Percent of Subtotal') {
      if (
        !serviceFee.percentValue ||
        isNaN(serviceFee.percentValue) ||
        serviceFee.percentValue <= 0
      ) {
        errors.push('Percent value must be greater than 0')
      }
    }
  })

  return errors.join(', ')
}

const renderServiceFeeInput = (
  serviceFee: GroupOrderCustomServiceFee,
  onChangeServiceFee: (field: string) => (value: number) => void,
) => {
  if (serviceFee.valueCalculationType === 'Flat Price') {
    return (
      <CurrencyInput
        label="Value"
        width="50%"
        value={serviceFee.value}
        prefix="$"
        delay={5}
        onChange={(value: number) => onChangeServiceFee('value')(value)}
      />
    )
  }

  return (
    <CurrencyInput
      label="Percent Value"
      width="50%"
      value={serviceFee.percentValue}
      prefix=""
      suffix="%"
      delay={5}
      onChange={(value: number) => onChangeServiceFee('percentValue')(value)}
    />
  )
}

interface FormState {
  globalCustomServiceFeesEnabled: boolean
  globalConfig: GroupOrderCustomServiceFeeSettings
  locationCustomServiceFeesEnabled: boolean
  locationConfigs: Record<
    string,
    {
      id: string
      name: string
      config: GroupOrderCustomServiceFeeSettings
    }
  >
}

interface EditGroupOrderCustomServiceFeesModalProps {
  client?: {
    name: string
    accountCustomServiceFees: AccountCustomServiceFee[]
  }
  clientGlobalSettings: {
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings | null
  }
  clientSettingsCollection: {
    id: string
    name: string
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings | null
  }[]
  onSaveCustomServiceFeeSettings: (
    globalConfig: GroupOrderCustomServiceFeeSettings | null,
    locationConfigs: Record<string, GroupOrderCustomServiceFeeSettings | null>,
  ) => Promise<boolean>
  closeModal: () => void
  flashError: (message: string) => void
}

const EditGroupOrderCustomServiceFeesModal: React.FC<
  EditGroupOrderCustomServiceFeesModalProps
> = ({
  client,
  clientGlobalSettings,
  clientSettingsCollection,
  onSaveCustomServiceFeeSettings,
  closeModal,
  flashError,
}) => {
  const [form, setForm] = useState<FormState | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (client && clientGlobalSettings && clientSettingsCollection) {
      setForm(initForm(client, clientGlobalSettings, clientSettingsCollection))
    }
  }, [client, clientGlobalSettings, clientSettingsCollection])

  const onSave = async () => {
    if (!form) {
      return
    }

    if (!client || !clientGlobalSettings) {
      flashError('Cannot save because client information is not available')

      return
    }

    if (client && client.accountCustomServiceFees.length === 0) {
      flashError(
        'Cannot create custom service fees if the client has no custom service fees configured',
      )

      return
    }

    const {
      globalCustomServiceFeesEnabled,
      locationCustomServiceFeesEnabled,
      globalConfig,
      locationConfigs,
    } = form

    if (!(globalCustomServiceFeesEnabled || locationCustomServiceFeesEnabled)) {
      flashError(
        'Select an option for how custom service fees should be applied',
      )

      return
    }

    let nextGlobalConfig = null
    const nextLocationsConfig: Record<
      string,
      GroupOrderCustomServiceFeeSettings | null
    > = {}
    if (globalCustomServiceFeesEnabled && globalConfig) {
      const globalConfigErrors = validateConfig(globalConfig)
      if (globalConfigErrors) {
        flashError(globalConfigErrors)

        return
      }

      nextGlobalConfig = globalConfig
      // Rm any location specific custom service fee settings if global enabled
      const locationsWithCustomServiceFeeSettings =
        clientSettingsCollection.filter((cs: any) => {
          return Boolean(cs.customServiceFeesSettings)
        })
      locationsWithCustomServiceFeeSettings.forEach((cs: any) => {
        nextLocationsConfig[cs.id] = null
      })
    } else if (locationCustomServiceFeesEnabled && locationConfigs) {
      const locationsConfigErrors = Object.values(locationConfigs).some(
        (csConfig) => {
          const configErrors = validateConfig(csConfig.config)

          return Boolean(configErrors)
        },
      )
      if (locationsConfigErrors) {
        flashError('Invalid location specific custom service fee settings')

        return
      }
      nextGlobalConfig = null
      Object.values(locationConfigs).forEach((csConfig) => {
        nextLocationsConfig[csConfig.id] = csConfig.config
      })
    } else {
      // Clear all custom service fee settings at global and location level
      nextGlobalConfig = null
      const locationsWithCustomServiceFeeSettings =
        clientSettingsCollection.filter((cs: any) => {
          return Boolean(cs.customServiceFeesSettings)
        })
      locationsWithCustomServiceFeeSettings.forEach((cs: any) => {
        nextLocationsConfig[cs.id] = null
      })
    }

    setLoading(true)
    const result = await onSaveCustomServiceFeeSettings(
      nextGlobalConfig,
      nextLocationsConfig,
    )
    setLoading(false)

    if (result) {
      closeModal()
    }
  }

  const onChangeGlobalCustomServiceFeesEnabled = (value: boolean) => {
    form &&
      setForm({
        ...form,
        globalCustomServiceFeesEnabled: value,
        locationCustomServiceFeesEnabled: false,
      })
  }

  const onChangeLocationCustomServiceFeesEnabled = (value: boolean) => {
    form &&
      setForm({
        ...form,
        locationCustomServiceFeesEnabled: value,
        globalCustomServiceFeesEnabled: false,
      })
  }

  const onChangeGlobalServiceFee =
    (idx: number) => (field: string) => (value: number) => {
      if (!form) {
        return
      }

      const nextGlobalConfig = [...form.globalConfig.serviceFees]
      nextGlobalConfig[idx] = {
        ...nextGlobalConfig[idx],
        [field]: value,
      }

      setForm({
        ...form,
        globalConfig: {
          serviceFees: nextGlobalConfig,
        },
      })
    }

  const onChangeLocationServiceFee =
    (locationId: string, idx: number) => (field: string) => (value: number) => {
      if (!form) {
        return
      }

      const locationConfig = form.locationConfigs[locationId]
      const nextServiceFees = [...locationConfig.config.serviceFees]
      nextServiceFees[idx] = {
        ...nextServiceFees[idx],
        [field]: value,
      }

      setForm({
        ...form,
        locationConfigs: {
          ...form.locationConfigs,
          [locationId]: {
            ...locationConfig,
            config: {
              serviceFees: nextServiceFees,
            },
          },
        },
      })
    }

  const renderContent = () => {
    if (!form) {
      return renderNoData()
    }

    const {
      globalCustomServiceFeesEnabled,
      globalConfig,
      locationCustomServiceFeesEnabled,
      locationConfigs,
    } = form

    return (
      <div className="edit-group-order-custom-service-fee-settings-modal">
        <div className="info-container">
          <p>
            Custom service fees can apply to all locations or work on a
            location-specific basis.
          </p>
        </div>
        <div className="all-configs-container">
          <div>
            <Checkbox
              checked={globalCustomServiceFeesEnabled}
              label="Global custom service fees"
              marginBottom="20px"
              value="globalCustomServiceFeesEnabled"
              onClick={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeGlobalCustomServiceFeesEnabled(e.target.checked)
              }
            />
          </div>
          {globalCustomServiceFeesEnabled && globalConfig && (
            <div className="global-config-container">
              {globalConfig.serviceFees.map(
                (serviceFee: GroupOrderCustomServiceFee, idx: number) => (
                  <div
                    key={`${serviceFee.serviceFeeType}-${serviceFee.feeDescription}-${idx}`}
                    className="service-fee-container"
                  >
                    <FlexContainer width="100%" flexDirection="column">
                      <div className="service-fee-title">
                        {serviceFee.serviceFeeType} -{' '}
                        {serviceFee.feeDescription}
                      </div>
                      <div className="service-fee-details">
                        {serviceFee.valueCalculationType}
                        {!serviceFee.isTaxExempt
                          ? ` - Taxable: ${serviceFee.itemTaxCode}`
                          : ''}
                      </div>
                    </FlexContainer>
                    {renderServiceFeeInput(
                      serviceFee,
                      onChangeGlobalServiceFee(idx),
                    )}
                  </div>
                ),
              )}
            </div>
          )}
          <div>
            <Checkbox
              checked={locationCustomServiceFeesEnabled}
              label="Per-location custom service fees"
              marginBottom="20px"
              value="locationCustomServiceFeesEnabled"
              onClick={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeLocationCustomServiceFeesEnabled(e.target.checked)
              }
            />
          </div>
          {locationCustomServiceFeesEnabled && locationConfigs && (
            <div>
              {Object.values(locationConfigs).map((csConfig) => (
                <div key={csConfig.id} className="location-container">
                  <div className="location-title">
                    {csConfig.name.length > 0
                      ? csConfig.name
                      : 'Default Location'}
                  </div>
                  {csConfig.config.serviceFees.map(
                    (serviceFee: GroupOrderCustomServiceFee, idx: number) => (
                      <div
                        key={`${serviceFee.serviceFeeType}-${serviceFee.feeDescription}-${idx}`}
                        style={{ marginBottom: '10px' }}
                        className="service-fee-container"
                      >
                        <FlexContainer width="100%" flexDirection="column">
                          <div className="service-fee-title">
                            {serviceFee.serviceFeeType} -{' '}
                            {serviceFee.feeDescription}
                          </div>
                          <div className="service-fee-details">
                            {serviceFee.valueCalculationType}
                            {!serviceFee.isTaxExempt
                              ? ` - Taxable: ${serviceFee.itemTaxCode}`
                              : ''}
                          </div>
                        </FlexContainer>
                        {renderServiceFeeInput(
                          serviceFee,
                          onChangeLocationServiceFee(csConfig.id, idx),
                        )}
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="buttons-container">
          <LinkText
            label="Cancel"
            onClick={() => closeModal()}
            color="#b30303"
          />
          <Button label="Save" onClick={onSave} disabled={loading} />
        </div>
      </div>
    )
  }

  const renderNoData = () => {
    return <div>Client global settings not available</div>
  }

  return (
    <Modal
      title="Custom Service Fees"
      hideModal={closeModal}
      color="#001940"
      maxWidth="400px"
      width="400px"
      height="600px"
    >
      {client && clientGlobalSettings ? renderContent() : renderNoData()}
    </Modal>
  )
}

export default EditGroupOrderCustomServiceFeesModal
